import { forwardRef, useId } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/cn";

// Labeled <select>, structurally IDENTICAL to Input so the two line up when they
// sit side by side in a grid.
//
// Every form that needed a dropdown hand-rolled one, and none of them matched the
// text fields beside them: the label was `text-xs font-semibold uppercase
// text-slate-400` against Input's `text-sm font-medium text-slate-700`, the
// control was `rounded-lg px-3 py-2` (~40px) against Input's `h-11 rounded-xl
// px-4` (44px), and there was no error slot at all. In a two-column grid that
// produced two fields whose labels sat on different baselines and whose boxes
// were different heights and radii — the Coupons form's Type / Percent pair being
// the visible case.
//
// Same props as Input (`label`, `error`, `required`, `id`, `className`) for the
// same reasons, so swapping one for the other is never a layout change.
const Select = forwardRef(function Select(
  { label, error, className, id, required, children, ...props },
  ref
) {
  const autoId = useId();
  const fieldId = id || autoId;
  const errorId = `${fieldId}-error`;
  return (
    <label className="block" htmlFor={fieldId}>
      {label && (
        <span className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
          {label}
          {required && <span className="text-rose-500" aria-hidden="true"> *</span>}
        </span>
      )}
      <span className="relative block">
        <select
          ref={ref}
          id={fieldId}
          required={required}
          aria-required={required ? "true" : undefined}
          aria-invalid={error ? "true" : undefined}
          aria-describedby={error ? errorId : undefined}
          className={cn(
            // `appearance-none` + our own chevron: the native arrow is drawn at a
            // different size in every browser, which is the other half of why a
            // select never quite matched the input next to it. pr-10 leaves room
            // for the chevron so a long option label can't run under it.
            "h-11 w-full appearance-none rounded-xl border border-slate-300 bg-white px-4 pr-10 text-sm text-slate-900 transition-colors focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-500/15 dark:border-white/10 dark:bg-white/5 dark:text-white",
            error && "border-rose-400 focus:border-rose-500 focus:ring-rose-500/15",
            className
          )}
          {...props}
        >
          {children}
        </select>
        <ChevronDown
          size={16}
          aria-hidden="true"
          className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400"
        />
      </span>
      {error && (
        <span id={errorId} role="alert" className="mt-1.5 block text-xs font-medium text-rose-500">
          {error}
        </span>
      )}
    </label>
  );
});

export default Select;
